import { useQuery } from '@tanstack/react-query';
import { WifiOff } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiJson } from '@/lib/api';
import { EmptyState, Spinner } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { useFormat } from '@/lib/format';

export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Reports run on the server and need internet (requirements §6.9, NF-1). */
export function useReport<T>(path: string | null) {
  const online = useOnline();
  const q = useQuery({ queryKey: ['report', path], queryFn: () => apiJson<T>(path!), enabled: online && !!path });
  return { ...q, online };
}

export function ReportState({ online, isLoading, error, children }: { online: boolean; isLoading: boolean; error: unknown; children: ReactNode }) {
  const { t } = useTranslation();
  if (!online) {
    return (
      <EmptyState>
        <WifiOff className="mx-auto mb-2 h-8 w-8" />
        {t('reports.needsInternet')}
      </EmptyState>
    );
  }
  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }
  if (error) return <EmptyState>{errorMessage(t, error)}</EmptyState>;
  return <>{children}</>;
}

export function DataAsOf({ iso }: { iso: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  return <p className="text-sm text-stone-500">{t('reports.dataAsOf', { time: f.time(iso) })}</p>;
}

/** "+12%" / "−5%" with a tone for tiles. */
export function change(value: number | null | undefined, previous: number | null | undefined, higherIsBetter = true) {
  if (value === null || value === undefined || previous === null || previous === undefined || previous === 0) return null;
  const pct = Math.round(((value - previous) / Math.abs(previous)) * 100);
  const tone: 'up' | 'down' | 'none' = pct === 0 ? 'none' : (pct > 0) === higherIsBetter ? 'up' : 'down';
  return { label: `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(pct)}%`, tone };
}
